import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { editMasterBudgetSchema, EditMasterBudgetInput } from "./masterBudgetSchema";
import { useUpdateMasterBudget } from "@/hooks/useMasterBudgets";
import { useUpdateMonthlyBudget } from "@/hooks/useMonthlyBudgets";
import { useState } from "react";

interface EditBudgetDialogProps {
  type: "master" | "monthly";
  id: string;
  /** Current name (master) or title (monthly) */
  currentName: string;
  currentDescription?: string | null;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

export function EditBudgetDialog({
  type,
  id,
  currentName,
  currentDescription,
  trigger,
  onSuccess,
}: EditBudgetDialogProps) {
  const [open, setOpen] = useState(false);

  const updateMaster = useUpdateMasterBudget();
  const updateMonthly = useUpdateMonthlyBudget();

  const isPending = type === "master" ? updateMaster.isPending : updateMonthly.isPending;

  const nameLabel = type === "master" ? "Name" : "Title";

  const form = useForm<EditMasterBudgetInput>({
    resolver: zodResolver(editMasterBudgetSchema),
    values: {
      name: currentName,
      description: currentDescription ?? "",
    },
  });

  const onSubmit = async (data: EditMasterBudgetInput) => {
    if (type === "master") {
      await updateMaster.mutateAsync({ id, name: data.name, description: data.description });
    } else {
      await updateMonthly.mutateAsync({ id, title: data.name, description: data.description });
    }
    setOpen(false);
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? <Button variant="outline">Edit</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Edit {type === "master" ? "Master Budget" : "Monthly Budget"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{nameLabel}</FormLabel>
                  <FormControl>
                    <Input placeholder={`Enter ${nameLabel.toLowerCase()}`} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Brief description"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
